import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, api, type Uuid } from "@shared/api";
import { plural, time } from "@shared/lib/format";
import { Card, Row } from "@shared/ui/settings-card";
import { RadioGroup, RadioItem } from "@shared/ui/radio";
import { Switch } from "@shared/ui/switch";
import { IconTimer } from "@shared/ui/icons";

/** Уровни проверки — как в Discord, без почты и телефона: их у нас не подтверждают. */
const LEVELS = [
  { value: 0, title: "Нет", note: "Новичок пишет сразу." },
  { value: 1, title: "Средний", note: "Аккаунт зарегистрирован больше 5 минут назад." },
  { value: 2, title: "Высокий", note: "И на сервере больше 10 минут." },
];

/**
 * Безопасность сервера — как «Защита от налётов» в Discord: уровень проверки
 * новичков и пауза приглашений. Сохраняется сразу, без кнопки: в налёт каждая
 * секунда на счету.
 */
export function SafetyTab({
  serverId,
  verification,
  paused,
  onChanged,
}: {
  serverId: Uuid;
  verification: number;
  paused: boolean;
  onChanged: () => void;
}) {
  const [level, setLevel] = useState(verification);
  const [pause, setPause] = useState(paused);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => setLevel(verification), [verification]);
  useEffect(() => setPause(paused), [paused]);

  async function save(body: { verification?: number; invites_paused?: boolean }, undo: () => void) {
    setBusy(true);
    setProblem(null);
    try {
      await api.servers.update(serverId, body);
      onChanged();
    } catch (error) {
      undo();
      setProblem(error instanceof ApiError ? error.message : "Не сохранилось");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="automod">
      <Card title="Уровень проверки">
        <div className="safety-levels">
          <RadioGroup
            value={String(level)}
            aria-label="Уровень проверки"
            onValueChange={(next) => {
              const before = level;
              setLevel(Number(next));
              void save({ verification: Number(next) }, () => setLevel(before));
            }}
          >
            {LEVELS.map((item) => (
              <RadioItem
                key={item.value}
                value={String(item.value)}
                title={item.title}
                note={item.note}
                disabled={busy}
              />
            ))}
          </RadioGroup>
          <div className="small muted">
            Пока срок не вышел, новичок только читает. Владелец и все, у кого есть роль, не ждут.
          </div>
        </div>
      </Card>
      <Card title="Приглашения">
        <Row
          label
          title="Приостановить приглашения"
          note="По кодам никто не вступит, пока не снимете паузу. Сами коды останутся."
        >
          <Switch
            checked={pause}
            disabled={busy}
            onCheckedChange={(next) => {
              setPause(next);
              void save({ invites_paused: next }, () => setPause(!next));
            }}
          />
        </Row>
      </Card>
      {problem && <div className="small danger-text">{problem}</div>}
    </div>
  );
}

/**
 * Полоса вместо поля ввода у новичка, который ещё ждёт срока проверки. Срок
 * вышел — права перечитываются сами: событий об этом сервер не шлёт.
 */
export function WaitBar({ until }: { until: string }) {
  const cache = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  const at = new Date(until).getTime();

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 15_000);
    const done = window.setTimeout(
      () => void cache.invalidateQueries({ queryKey: ["rights"] }),
      Math.max(0, at - Date.now()) + 1_000,
    );
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(done);
    };
  }, [at, cache]);

  const minutes = Math.max(1, Math.ceil((at - now) / 60_000));

  return (
    <div className="rules-bar">
      <IconTimer size={16} />
      <span className="grow">
        На сервере проверка новичков: писать можно будет в {time(until)} — через {minutes}{" "}
        {plural(minutes, "минуту", "минуты", "минут")}.
      </span>
    </div>
  );
}
